// `chrome-extension.extension-tabs.client-contacts.hunter.edit-alert` — the contact criteria
// builder (plan §7 phase 4).
//
// ⚠️ THIS ROUTE USED TO FALL BACK TO `PendingSlice` ON PURPOSE, and the note that said so was
// right: `extension-alert-edit.tpl.html` picks `<chrome-extension-search-page>` or
// `<client-contact-search-page>` on the product, and serving the candidate builder here would
// have rendered a form for the wrong entity. This is that second form.
//
// What it reuses from the candidate side is the FRAME, not the form: `AlertEditFrame` for the
// rail and the alert name, `CriteriaNavigation` for the section list, `WooCollapsible` for the
// folds. The sections themselves share nothing — different criteria, different controls.
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  AlertEntity,
  fetchViewModel,
  getAlerts,
  saveAlert,
  emptyAlert,
  NEW_ALERT_ID,
  getHighestActivityLevels,
  getHighestActivityLevelTags,
  getJobTitles,
  getTitleRecommendations,
  type TitleRecommendation,
  getCompanySizes,
  getCompanyTypes,
  getRootTitleClasses,
  getExtensionIndustries,
  getCities,
  getLocationsOrRegionsFull,
  getAtsFiltersConfiguration,
  getAtsFiltersSingleselectValues,
  getClientContactCriteriaPrediction,
  type AtsFiltersFieldConfiguration,
  type AtsFiltersSingleselectValues,
  type AtsFiltersWhitelabelConfiguration,
  type ExtensionAlertContainer,
} from '@woo-ext/api'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import {
  AlertEditFrame,
  type AlertTabPrediction,
  AlertTabState,
  AtsFiltersSection,
  atsFiltersHaveErrors,
  type AtsFilterRow,
  ClientContactCriteriaShell,
  ContactFiltersSection,
  CompanyFiltersSection,
  clientContactNavigationOptions,
  CriteriaNavigation,
  ExtensionAlertsHost,
  ExtensionAlertTab,
  KeywordSearchSection,
  CLIENT_CONTACT_KEYWORD_SECTION_OPTIONS,
  type KeywordCondition,
  type ClientContactFormCriteria,
  EMPTY_CLIENT_CONTACT_CRITERIA,
  emptyClientContactKeywordCondition,
  loadClientContactCriteria,
  mapClientContactCriteria,
  isAtsFilterDataValid,
  loadAtsFilters,
  loadKeywords,
  loadLocation,
  LocationSection,
  type LocationCriteria,
  regionsForCountry,
  usedNavigationOptions,
  getAtsLabel,
} from '@woo-ext/ui'
import { ExtensionProduct, removeLastAlertViewed } from './lastAlertViewed.ts'

export function ClientContactEditAlert(): ReactElement {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [containers, setContainers] = useState<ExtensionAlertContainer[] | undefined>(undefined)
  const [jobCompanyInfoEnabled, setJobCompanyInfoEnabled] = useState(false)
  /**
   * ⚠️ THE SAME RACE THE CANDIDATE SCREEN HAD, and it bites harder here because the flag picks
   * the KEY. `loadClientContactCriteria` reads `companyLocationCriteria` with the flag on and
   * `locationCriteria` with it off; the seeding effect fires on the first render, before
   * `fetchViewModel` answers, and its `seededAlertId` guard then refuses the re-seed when the
   * flag lands. Measured: a saved alert carrying `companyLocationCriteria` reopened with an
   * EMPTY location section, because the seed had read the other key.
   */
  const [bootstrapReady, setBootstrapReady] = useState(false)
  /** See the candidate screen: the bootstrap gate lets alerts arrive first, and a Save in
   *  that window writes `EMPTY_CLIENT_CONTACT_CRITERIA` over the alert's own criteria. */
  const [seededFor, setSeededFor] = useState<number | undefined>(undefined)
  // The ATS LABEL (`getAtsLabel`, 5cfd3ef899): every use on this screen is a title.
  const [atsLabel, setAtsLabel] = useState<string | undefined>(undefined)
  const [hasAtsFilters, setHasAtsFilters] = useState(false)
  const [prediction, setPrediction] = useState<AlertTabPrediction | undefined>(undefined)
  const [atsFilterFields, setAtsFilterFields] = useState<readonly AtsFiltersFieldConfiguration[]>([])
  const [singleselectValues, setSingleselectValues] = useState<AtsFiltersSingleselectValues>({})
  // ⚠️ ONE SOURCE OF TRUTH, AND THE SECOND ONE WAS A BUG FACTORY. Location, the ATS filter
  // rows and the keyword conditions used to live in states of their own, copied out of `form`
  // by an effect. That made the criteria object rebuild on a render where only the copy had
  // moved — so `criteria` was a NEW OBJECT every render, the prediction effect (keyed on it)
  // re-fired in a loop, and `isCriteriaChanged` read true on a form nobody had touched. Both
  // of those were reported. `form` holds all of it now.
  /**
   * ⚠️ THE SCREEN HAD NO FORM STATE AT ALL. Every section was rendered from the ALERT's saved
   * criteria and collected nothing back, so the two filter sections were read-only, the Save
   * row was never drawn, and there was nothing for a Save to send. This is the contacts twin
   * of the candidate screen's criteria state; `clientContactCriteria.ts` is both mappings.
   */
  const [form, setForm] = useState<ClientContactFormCriteria>(EMPTY_CLIENT_CONTACT_CRITERIA)
  const [saving, setSaving] = useState(false)
  /**
   * `editedExtensionAlert.name` — the rename, held here because the frame only draws it.
   *
   * ⚠️ `undefined` MEANS "NOT RENAMED", NOT "EMPTY". Seeding it from the loaded alert would
   * pin the first render's value, and the alert arrives asynchronously — the field would
   * start blank and stay blank. The fallback is read at use, so an untouched form saves the
   * name it loaded with.
   */
  const [editedName, setEditedName] = useState<string | undefined>(undefined)

  const [engagementSuggestions, setEngagementSuggestions] = useState<readonly { text: string }[]>([])
  /**
   * `titles.titleRecommendations`, per row — the contact form's own copy of the candidate
   * builder's state. Upstream keeps it on each `IClientContactTempTitles` and refreshes it
   * from `getTitleRecommendationsOnTitlesAddedRemoved`, which fires on exactly this change.
   */
  const [titleRecommendations, setTitleRecommendations] = useState<
    Record<number, readonly TitleRecommendation[]>
  >({})
  const [recommendationsLoading, setRecommendationsLoading] = useState(false)
  const loadedCriteria = useRef<string | undefined>(undefined)
  const seededAlertId = useRef<number | undefined>(undefined)
  const [regionsByCountry, setRegionsByCountry] = useState<
    Readonly<Record<string, readonly { id: number; name: string; displayText: string; showOnMetroList?: boolean }[]>> | undefined
  >(undefined)

  const raw = params.get('alertId')
  const parsed = raw === null ? Number.NaN : Number.parseInt(raw, 10)
  const alertId = Number.isNaN(parsed) ? undefined : parsed

  // The edit state's `onEnter`: "always unset lastAlertViewedState when entering edit alert (if
  // last alert viewed is the edited alert)" — an edited search matches different rows, so the
  // stored ids would replay a result set that no longer exists.
  useEffect(() => {
    if (alertId === undefined) return
    void removeLastAlertViewed(ExtensionProduct.ClientContacts, alertId)
  }, [alertId])

  useEffect(() => {
    let live = true
    getAlerts(AlertEntity.ClientContacts).then(
      (loaded) => {
        if (live) setContainers(loaded)
      },
      () => undefined,
    )
    fetchViewModel().then(
      (model) => {
        if (!live) return
        setAtsLabel(getAtsLabel(model))
        setRegionsByCountry(model.extensionRegionDetailsByCountry)
        // ⚠️ THE VIEW MODEL DOES CARRY IT — as `featureFlags.JOB_COMPANY_INFO`. This screen
        // hardcoded the capability off under a note saying otherwise, which cost it three
        // things at once: the "Company location" section (and its `companyLocationCriteria`
        // key), the high-supply checkbox, and the Size and Type fields. Measured against
        // production build 354 with the flag on, which rendered all of them and saved
        // `companyLocationCriteria`, `sizeCriteria`, `typeCriteria` and `supplyCriteria`.
        setJobCompanyInfoEnabled(model.featureFlags?.['JOB_COMPANY_INFO'] === true)
        setBootstrapReady(true)
      },
      () => {
        // ⚠️ THE GATE OPENS ON FAILURE TOO. A view model that never answers must not leave the
        // form permanently unseeded — the flags simply keep their defaults.
        setBootstrapReady(true)
      },
    )
    // ⚠️ THE CONFIGURATION IS PER ENTITY, AND THE PARAMETER IS THE ENUM'S **NAME**. Upstream
    // writes `EntityType[entityType]` — a reverse lookup — so the request carries
    // `entityType=CLIENT_CONTACT`, not the ordinal 11. Sending the number returns the
    // candidate configuration, which is the wrong section on the wrong form. And the call is
    // per entity for a reason: a tenant can have filterable candidate fields and no contact
    // ones, so reusing the candidate answer adds a section this form cannot fill.
    getAtsFiltersConfiguration('CLIENT_CONTACT').then(
      (configuration: AtsFiltersWhitelabelConfiguration) => {
        if (!live) return
        const fields = configuration.fields ?? []
        setHasAtsFilters(fields.length > 0)
        setAtsFilterFields(fields)
        const sections = [...new Set(fields.map((field) => field.section).filter((s_): s_ is string => s_ !== undefined))]
        if (sections.length === 0) return
        // Same entity name as the configuration call — the values are per entity too.
        getAtsFiltersSingleselectValues(sections, 'CLIENT_CONTACT').then(
          (values) => {
            if (live) setSingleselectValues(values)
          },
          () => undefined,
        )
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [])

  // ⚠️ `NEW_ALERT_ID` BRANCHES BEFORE THE FALLBACK, exactly as on the candidate screen — and
  // this screen did not, which was worse here than the candidates one would have been. `-1`
  // matches no container, so `?? containers?.[0]` resolved "Add search" to the user's MOST
  // RECENT saved search and seeded the form with its criteria; `current.alert.id` then rode
  // into `saveAlert`, so Save did not create a new alert, it OVERWROTE that one.
  //
  // Measured against production build 354 on the same tenant: production's save carried no
  // `id` and created alerts 21, 22 and 23, while this screen's carried `id=21`, `id=22`,
  // `id=23` — each time the alert production had created moments earlier. `containers[0]` is
  // the newest alert, so the only thing standing between this and a recruiter's live search
  // was which alert happened to be first in the list.
  const blank = useMemo<ExtensionAlertContainer>(() => ({ id: NEW_ALERT_ID, alert: emptyAlert() }), [])
  const current =
    alertId === NEW_ALERT_ID
      ? blank
      : (containers?.find((container) => container.alert.id === alertId) ?? containers?.[0])

  // ⚠️ SEEDED ONCE PER ALERT. Re-running on every render would put the saved criteria back
  // over whatever the user has just typed — the same rule the review screens' filter sync has.
  useEffect(() => {
    if (!bootstrapReady) return
    if (current === undefined || seededAlertId.current === current.alert.id) return
    seededAlertId.current = current.alert.id
    const seeded = loadClientContactCriteria(
      current.alert.extensionCriteria ?? undefined,
      (value) => loadLocation(value as Parameters<typeof loadLocation>[0]),
      (value) => loadAtsFilters(value as Parameters<typeof loadAtsFilters>[0]),
      // The contact empty condition, not the candidate one — see `loadKeywords`.
      (value) => loadKeywords(value as Parameters<typeof loadKeywords>[0], emptyClientContactKeywordCondition),
      jobCompanyInfoEnabled,
      // ⚠️ THE CONTAINER'S `extraInfo`, NOT THE ALERT'S. Saved titles are bare strings; this
      // is the only place the title-CLASS metadata survives, and without it a reopened alert
      // loses the "(5 titles)" marker production shows.
      current.extraInfo?.titlesExtraInfo ?? [],
    )
    setForm(seeded)
    setSeededFor(current.alert.id)
    // ⚠️ THE BASELINE IS THE SEEDED FORM, NOT THE FIRST RENDER'S. Taking it on first render
    // captured the EMPTY form, because the seed lands a render later — so `isCriteriaChanged`
    // was true the moment the alert loaded and every Save re-ran the alert's search. Measured
    // on the wire: `isCriteriaChanged=true` on a form nobody had touched.
    loadedCriteria.current = JSON.stringify(
      mapClientContactCriteria(
        seeded,
        regionsForCountry(regionsByCountry, seeded.location.country),
        seeded.atsFilters.filter((row) => isAtsFilterDataValid(row.data)),
        current.alert.extensionCriteria ?? undefined,
        jobCompanyInfoEnabled,
      ),
    )
  }, [bootstrapReady, current, regionsByCountry, jobCompanyInfoEnabled])

  const regions = regionsForCountry(regionsByCountry, form.location.country)
  const criteria = useMemo(
    () =>
      mapClientContactCriteria(
        form,
        regions,
        // `isDataValid` — a half-filled row is not sent, exactly as on the candidate screen.
        form.atsFilters.filter((row) => isAtsFilterDataValid(row.data)),
        current?.alert.extensionCriteria ?? undefined,
        jobCompanyInfoEnabled,
      ),
    [form, regions, current, jobCompanyInfoEnabled],
  )
  const criteriaKey = JSON.stringify(criteria)

  const onSave = (): void => {
    if (current === undefined || saving) return
    if (seededFor !== current.alert.id) return
    const name = editedName ?? current.alert.name
    if (name === undefined || name === '') return
    setSaving(true)
    saveAlert(AlertEntity.ClientContacts, {
      id: current.alert.id,
      name,
      extensionCriteria: criteria,
      isCriteriaChanged: criteriaKey !== loadedCriteria.current,
    }).then(
      (updated) => {
        setSaving(false)
        loadedCriteria.current = criteriaKey
        setContainers((previous) => {
          const kept = (previous ?? []).filter(
            (container) => container.alert.id !== updated.alert.id && container.alert.id !== NEW_ALERT_ID,
          )
          return [...kept, updated]
        })
        // The same unconditional navigation the candidate screen does — see its note.
        void navigate(
          `/chrome-extension/extension/client-contact/review?alertId=${String(updated.alert.id)}&autoShowResults=true`,
        )
      },
      () => {
        setSaving(false)
      },
    )
  }

  const exit = (): void => {
    if (current === undefined) return
    void navigate(`/chrome-extension/extension/client-contact/review?alertId=${String(current.alert.id)}`)
  }

  /**
   * ⚠️ KEYED ON THE CRITERIA'S JSON, NOT ON THE OBJECT, AND DEBOUNCED — the candidate screen's
   * two rules, which this one did not have. Depending on the object re-ran the effect on every
   * render, each answer called `setPrediction`, and that re-rendered: a request loop against
   * `getPrediction` for as long as the screen was open. Reported as the page "abusing the get
   * prediction endpoint", and it is exactly that.
   *
   * `lastPredicted` stops a re-render with unchanged criteria asking again, and the second's
   * delay is upstream's (`getPrediction` cancels any pending timer).
   */
  // ⚠️ KEYED ON THE TITLES, NOT ON THE FORM, for the reason the candidate screen records: the
  // form object is rebuilt on every keystroke in every field, and a recommendation depends on
  // the titles alone. Keying it on the form is what turned the prediction call into a loop.
  const contactTitleKey = JSON.stringify(form.titleRows.map((row) => (row.titles ?? []).map((tag) => tag.text)))
  useEffect(() => {
    const perRow = JSON.parse(contactTitleKey) as string[][]
    let live = true
    setRecommendationsLoading(perRow.some((titles) => titles.length > 0))
    void Promise.all(
      perRow.map(async (titles) => (titles.length === 0 ? [] : getTitleRecommendations(titles).catch(() => []))),
    ).then((answers) => {
      if (!live) return
      setTitleRecommendations(Object.fromEntries(answers.map((rows, index) => [index, rows])))
      setRecommendationsLoading(false)
    })
    return () => {
      live = false
    }
  }, [contactTitleKey])

  const lastPredicted = useRef<string | undefined>(undefined)
  useEffect(() => {
    const alert = current?.alert
    if (alert === undefined) return undefined
    // An empty form publishes nothing rather than a zero — see the candidate screen's note.
    if (Object.keys(JSON.parse(criteriaKey) as Record<string, unknown>).length === 0) {
      setPrediction(undefined)
      return undefined
    }
    if (criteriaKey === lastPredicted.current) return undefined

    let live = true
    const timer = setTimeout(() => {
      lastPredicted.current = criteriaKey
      // `getFiltersForCountersAndPrediction` — `{ showHiddenCandidates: false }`, explicit
      // rather than defaulted, exactly as on the candidate side.
      getClientContactCriteriaPrediction({
        // ⚠️ `null` FOR A NEW ALERT — `-1` IS NOT AN ALERT ID, it is this client's own
        // sentinel. The candidate screen normalises it and this one did not, which was
        // invisible while "Add search" wrongly resolved to an EXISTING alert: the id it sent
        // was real. Fixing that resolution turned every prediction on a new contact search
        // into a failure. Measured against the tenant:
        //   {"alertId":-1,   …} -> 403
        //   {"alertId":null, …} -> 200 {"results":14175}
        // so the "Matched client contacts" count simply never arrives.
        alertId: alert.id === NEW_ALERT_ID ? null : alert.id,
        extensionCriteria: JSON.parse(criteriaKey) as Record<string, unknown>,
        filter: { showHiddenCandidates: false },
      }).then(
        (answer) => {
          // ⚠️ ONE NUMBER FILLS BOTH. The contact DTO has no `topMatches` — see the API note —
          // so the bar plots the total against itself and reads full, which is the reference.
          if (live) setPrediction({ prediction: answer.results, topMatches: answer.results })
        },
        () => undefined,
      )
    }, 1000)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [current, criteriaKey])
  // With the flag on the rail reads "Company location" in place of "Location" — one entry
  // swapped for another, never both, which is why the count stays at five either way.
  const navigationOptions = clientContactNavigationOptions({ atsLabel, hasAtsFilters, jobCompanyInfoEnabled })

  return (
    <ExtensionAlertsHost entity="clientContacts" minWidth={ExtensionViewWidth.Full}>
      <AlertEditFrame
        alertName={editedName ?? current?.alert.name}
        // ⚠️ WITHOUT THIS A NEW CONTACT ALERT CANNOT BE SAVED AT ALL. `newAlert` is what opens
        // the name editor and selects it (`AlertEditFrame`, upstream's
        // `$timeout(() => this.onEditName(true), 1000)`), and `onSave` below returns silently
        // on an empty name. While this screen resolved "Add search" to an EXISTING alert the
        // gap was invisible — the alert already had a name. Fixing that resolution turned it
        // into a dead Save button: measured against production build 354, which named and
        // saved the new alert while this made no request at all.
        newAlert={current?.alert.id === NEW_ALERT_ID}
        onAlertNameChange={setEditedName}
        // ⚠️ THE BOTTOM Save/Cancel ROW IS DRAWN BY THESE TWO PROPS AND NOTHING ELSE
        // (`AlertEditFrame`: `onSave === undefined && onCancel === undefined ? null : …`).
        // This screen passed neither, so the footer production has at the foot of the form was
        // absent — and the tab's own pair at the top had only a Cancel.
        onSave={onSave}
        onCancel={exit}
        tab={
          current === undefined ? null : (
            <ExtensionAlertTab
              alert={current.alert}
              state={AlertTabState.Edit}
              currentAlertId={alertId}
              entityPlural="client contacts"
              {...(prediction === undefined ? {} : { prediction })}
              onSave={onSave}
              onCancel={exit}
            />
          )
        }
        navigation={
          <CriteriaNavigation
            options={navigationOptions}
            used={usedNavigationOptions(navigationOptions, criteria)}
            onNavigate={(option) => {
              document.getElementById(option.elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
          />
        }
      >
        <ClientContactCriteriaShell
          // ⚠️ THE SHELL DECIDES WHICH LOCATION SECTION EXISTS, and it was never told. It
          // already carries both ids and filters on this flag (`location-criteria-id` when
          // off, `company-location-criteria-id` when on) — but the default is `false`, so with
          // the flag ON the shell rendered the plain Location section while the content map
          // supplied the company one. Result: no location section at all, and
          // `companyLocationCriteria` missing from the save where production sends it.
          jobCompanyInfoEnabled={jobCompanyInfoEnabled}
          atsLabel={atsLabel}
          hasAtsFilters={hasAtsFilters}
          sections={{
            'contact-filters-criteria-id': (
              <ContactFiltersSection
                switchedCompany={form.switchedCompany}
                switchedCompanyMin={form.switchedCompanyMin}
                switchedCompanyMax={form.switchedCompanyMax}
                previousEngagement={form.previousEngagement}
                previousEngagementSuggestions={engagementSuggestions}
                loadPreviousEngagement={(query) => getHighestActivityLevelTags(query)}
                loadTitles={(index, query) =>
                  getJobTitles(query, (form.titleRows[index]?.titles ?? []).map((tag) => tag.text))
                }
                titleRows={form.titleRows}
                recommendations={titleRecommendations}
                recommendationsLoading={recommendationsLoading}
                // `onSelectTitleRecommendation` — the picked title joins the row's titles.
                onSelectRecommendation={(index, title) => {
                  setForm((previous) => ({
                    ...previous,
                    titleRows: previous.titleRows.map((row, at) =>
                      at === index ? { titles: [...(row.titles ?? []), { text: title }] } : row,
                    ),
                  }))
                }}
                onSwitchedCompanyChange={(enabled) => {
                  // `onSwitchedCompaniesCriteriaChecked` clears BOTH years when the box is
                  // ticked, so re-enabling it does not bring back the pair the user cleared.
                  setForm((previous) => ({
                    ...previous,
                    switchedCompany: enabled,
                    ...(enabled ? { switchedCompanyMin: '', switchedCompanyMax: '' } : {}),
                  }))
                }}
                onSwitchedCompanyMinChange={(value) => {
                  setForm((previous) => ({ ...previous, switchedCompanyMin: value }))
                }}
                onSwitchedCompanyMaxChange={(value) => {
                  setForm((previous) => ({ ...previous, switchedCompanyMax: value }))
                }}
                onPreviousEngagementChange={(tags) => {
                  setForm((previous) => ({ ...previous, previousEngagement: [...tags] }))
                }}
                onPreviousEngagementQueryChange={(query) => {
                  void getHighestActivityLevels(query).then(
                    (values) => {
                      setEngagementSuggestions(values.map((value) => ({ text: value.text ?? '' })))
                    },
                    () => undefined,
                  )
                }}
                onTitlesChange={(index, tags) => {
                  setForm((previous) => ({
                    ...previous,
                    titleRows: previous.titleRows.map((row, at) => (at === index ? { titles: [...tags] } : row)),
                  }))
                }}
                onAddTitleRow={() => {
                  setForm((previous) => ({ ...previous, titleRows: [...previous.titleRows, {}] }))
                }}
                onRemoveTitleRow={(index) => {
                  setForm((previous) => ({
                    ...previous,
                    // Never fewer than one row — the section repeats it and upstream seeds one.
                    titleRows:
                      previous.titleRows.length <= 1
                        ? [{}]
                        : previous.titleRows.filter((_, at) => at !== index),
                  }))
                }}
              />
            ),
            'company-filters-criteria-id': (
              <CompanyFiltersSection
                highHiringLikelihood={form.highHiringLikelihood}
                highSupply={form.highSupply}
                industries={form.industries}
                companyNames={form.companyNames}
                companySizes={form.companySizes}
                companyTypes={form.companyTypes}
                jobCompanyInfoEnabled={jobCompanyInfoEnabled}
                {...(atsLabel === undefined ? {} : { atsLabel })}
                loadIndustries={(query) => getExtensionIndustries(query)}
                loadCompanySizes={(query) => getCompanySizes(query)}
                loadCompanyTypes={(query) => getCompanyTypes(query)}
                onCompanySizesChange={(tags) => {
                  setForm((previous) => ({ ...previous, companySizes: [...tags] }))
                }}
                onCompanyTypesChange={(tags) => {
                  setForm((previous) => ({ ...previous, companyTypes: [...tags] }))
                }}
                hiringCategories={form.hiringCategories}
                {...(form.openJobsMin === undefined ? {} : { openJobsMin: form.openJobsMin })}
                {...(form.lastOpenedJobDaysAgo === undefined ? {} : { lastOpenedJobDaysAgo: form.lastOpenedJobDaysAgo })}
                loadHiringCategories={(query) => getRootTitleClasses(query)}
                onHiringCategoriesChange={(tags) => {
                  setForm((previous) => ({ ...previous, hiringCategories: [...tags] }))
                }}
                onOpenJobsMinChange={(value) => {
                  setForm((previous) => {
                    const { openJobsMin: _drop, ...rest } = previous
                    return value === undefined ? rest : { ...rest, openJobsMin: value }
                  })
                }}
                onLastOpenedJobChange={(value) => {
                  setForm((previous) => {
                    const { lastOpenedJobDaysAgo: _drop, ...rest } = previous
                    return value === undefined ? rest : { ...rest, lastOpenedJobDaysAgo: value }
                  })
                }}
                onHighHiringLikelihoodChange={(enabled) => {
                  setForm((previous) => ({ ...previous, highHiringLikelihood: enabled }))
                }}
                onHighSupplyChange={(enabled) => {
                  setForm((previous) => ({ ...previous, highSupply: enabled }))
                }}
                onIndustriesChange={(tags) => {
                  setForm((previous) => ({ ...previous, industries: [...tags] }))
                }}
                onCompanyNamesChange={(tags) => {
                  setForm((previous) => ({ ...previous, companyNames: [...tags] }))
                }}
              />
            ),
            // ⚠️ THREE OF THE FOUR REMAINING SECTIONS ARE THE CANDIDATE BUILDER'S, UNCHANGED.
            // A whitespace-insensitive diff of the contact Location section against the
            // candidate one is the criteria KEY (`locationCriteria` against `location`) and the
            // `order`/`ng-show` attributes — 238 lines against 235, and nothing else. Keyword
            // search and the ATS filters are the same story. Writing contact copies of them
            // would have been three files of duplication to render identical markup.
            //
            // Company location is the fourth, and it needs nothing: it renders only with
            // `jobCompanyInfoEnabled`, which this tenant does not have, and the shell already
            // swaps it out for Location.
            [jobCompanyInfoEnabled ? 'company-location-criteria-id' : 'location-criteria-id']: (
              <LocationSection
                criteria={form.location}
                regions={regions}
                subject="contacts"
                loadDistanceCities={(query) => getCities(query, form.location.country, { allowNullCountry: true })}
                loadCities={(query) => getCities(query, form.location.country)}
                loadGlobalLocations={(query) => getLocationsOrRegionsFull(query, form.location.country)}
                onChange={(next) => {
                  setForm((previous) => ({ ...previous, location: next }))
                }}
              />
            ),
            'ats-filters-criteria-id': (
              <AtsFiltersSection
                rows={form.atsFilters}
                fields={atsFilterFields}
                singleselectValues={singleselectValues}
                // `ctrl.form.atsFilters.$error.validAtsFilters` — see `atsFiltersHaveErrors`.
                showErrors={atsFiltersHaveErrors(form.atsFilters)}
                onChange={(index, row) => {
                  setForm((previous) => ({
                    ...previous,
                    atsFilters: previous.atsFilters.map((existing, at) => (at === index ? row : existing)),
                  }))
                }}
                onRemove={(index) => {
                  setForm((previous) => ({
                    ...previous,
                    // The last row is RESET, not removed — `removeAtsFilterCriteria` branches
                    // on `length === 1` and puts `{}` back. The title rows above already do
                    // this; these two did not, so removing the only row left the section with
                    // no controls at all.
                    atsFilters: previous.atsFilters.length === 1 ? [{}] : previous.atsFilters.filter((_, at) => at !== index),
                  }))
                }}
                onAdd={() => {
                  setForm((previous) => ({ ...previous, atsFilters: [...previous.atsFilters, {}] }))
                }}
              />
            ),
            // `ng-show="… && state == SHORTLIST"` — the contact builder gates this the same way
            // the candidate one does, and the edit route IS the embedded state.
            'keywords-criteria-id': (
              <KeywordSearchSection
                conditions={form.keywords}
                // Two options, not the candidate builder's four — and different ordinals.
                sectionOptions={CLIENT_CONTACT_KEYWORD_SECTION_OPTIONS}
                onChange={(index, condition) => {
                  setForm((previous) => ({
                    ...previous,
                    keywords: previous.keywords.map((existing, at) => (at === index ? condition : existing)),
                  }))
                }}
                onRemove={(index) => {
                  setForm((previous) => ({
                    ...previous,
                    keywords:
                      previous.keywords.length === 1
                        // ⚠️ THE CONTACT HELPER, NOT THE CANDIDATE ONE. Its category is `0`;
                        // the candidate default is `3`, which this field cannot display — the
                        // select would read "Current experience" while the wire carried 3.
                        ? [emptyClientContactKeywordCondition()]
                        : previous.keywords.filter((_, at) => at !== index),
                  }))
                }}
                onAdd={() => {
                  setForm((previous) => ({ ...previous, keywords: [...previous.keywords, {}] }))
                }}
              />
            ),
          }}
        />
      </AlertEditFrame>
    </ExtensionAlertsHost>
  )
}
